import { cva, type VariantProps } from 'class-variance-authority';
import BoltIcon from '@/assets/icons/bolt-icon.svg?react';
import { cn } from '@/libs/utils/cn';
import { formatCompactNumber } from '@/libs/utils/format';
import { MetricBar } from '../atoms/metricbar';

/**
 * Abbreviate unit strings for compact display
 * e.g., "kWh/year" -> "kWh/y", "kWh per year" -> "kWh/y"
 */
function abbreviateUnit(unit: string): string {
  return unit
    .replace(/\/year/gi, '/y')
    .replace(/per year/gi, '/y')
    .replace(/\/month/gi, '/m')
    .replace(/per month/gi, '/m');
}

const wrapperVariants = cva('flex flex-col gap-1 min-w-0', {
  variants: {
    variant: {
      normal: '',
      main: 'gap-3',
    },
  },
  defaultVariants: { variant: 'normal' },
});

const containerVariants = cva('flex justify-between', {
  variants: {
    variant: {
      normal: 'min-w-0',
      main: 'flex-col gap-3',
    },
  },
  defaultVariants: { variant: 'normal' },
});

const labelVariants = cva('text-sm', {
  variants: {
    variant: {
      normal: 'text-muted-foreground max-w-[120px] line-clamp-2',
      main: 'text-primary-600',
    },
  },
  defaultVariants: { variant: 'normal' },
});

const valueContainerVariants = cva('font-bold text-sm text-primary-700', {
  variants: {
    variant: {
      normal: 'flex flex-col items-end flex-shrink-0',
      main: 'flex flex-nowrap items-center gap-1.5 whitespace-nowrap text-[2.125rem] tracking-tight font-light',
    },
  },
  defaultVariants: { variant: 'normal' },
});

const unitVariants = cva('', {
  variants: {
    variant: {
      normal: '',
      main: 'text-xs text-primary-700 font-normal ml-0.5',
    },
  },
  defaultVariants: { variant: 'normal' },
});

const metricBarWrapperVariants = cva('flex justify-between w-full gap-3', {
  variants: {
    variant: {
      normal: '',
      main: '',
    },
  },
  defaultVariants: { variant: 'normal' },
});

const changeDisplayVariants = cva('flex justify-start items-center gap-1.5 text-xs font-bold', {
  variants: {
    variant: {
      normal: 'hidden',
      main: 'text-primary-600',
    },
  },
  defaultVariants: { variant: 'normal' },
});

const changeBadgeVariants = cva('flex px-1.5 py-0.5 rounded-full justify-center', {
  variants: {
    isPositive: {
      true: 'bg-green-100',
      false: 'bg-red-50',
    },
  },
  defaultVariants: { isPositive: true },
});

const changeTextVariants = cva('font-bold text-[0.625rem] whitespace-nowrap', {
  variants: {
    isPositive: {
      true: 'text-green-700',
      false: 'text-red-600',
    },
  },
  defaultVariants: { isPositive: true },
});

interface MetricForecastProps extends VariantProps<typeof containerVariants> {
  label: string;
  value: number | string | null;
  unit?: string;
  showMetricBar?: boolean;
  /**
   * Secondary value shown beside the primary `value`. Rendered as a signed
   * contribution badge in the normal variant, or as the smaller figure next to
   * the bolt icon in the main variant.
   */
  secondaryValue?: number;
  /** Unit for the secondary value display */
  secondaryUnit?: string;
  /** Total value used to calculate the contribution percentage for MetricBar */
  totalValue?: number;
  formatValue?: (value: number) => string;
  /** Formatter for the secondary value. Defaults to `formatValue`. */
  formatSecondaryValue?: (value: number) => string;
}

export function MetricForecast({
  label,
  value,
  unit,
  variant = 'normal',
  showMetricBar = false,
  secondaryValue = 0,
  secondaryUnit,
  totalValue,
  formatValue,
  formatSecondaryValue = formatValue,
}: MetricForecastProps) {
  // Handle value - could be number, string (non-numeric like "Metropole"), or null
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  const isNumericValue = numericValue != null && !Number.isNaN(numericValue);

  let formattedValue: string | undefined;
  if (value == null) {
    formattedValue = undefined;
  } else if (isNumericValue && formatValue) {
    formattedValue = formatValue(numericValue);
  } else {
    formattedValue = String(value);
  }

  const isNumericSecondaryValue =
    typeof secondaryValue === 'number' && !Number.isNaN(secondaryValue);
  const isPositive = secondaryValue >= 0;
  const isMainVariant = variant === 'main';

  // Format contribution with compact number and abbreviated unit (e.g., "+500k kWh/y")
  const compactSecondaryValue = formatCompactNumber(secondaryValue);
  const abbreviatedUnit = secondaryUnit ? abbreviateUnit(secondaryUnit) : '';
  const formattedContribution = isNumericSecondaryValue
    ? `${isPositive ? '+' : ''}${compactSecondaryValue}${abbreviatedUnit ? ` ${abbreviatedUnit}` : ''}`
    : '';

  // Calculate percentage relative to total for MetricBar (-100% to +100%)
  const contributionPercent =
    isNumericSecondaryValue && totalValue ? (secondaryValue / totalValue) * 100 : 0;

  // Format for main variant display
  const formattedSecondaryValue =
    isNumericSecondaryValue && formatSecondaryValue
      ? formatSecondaryValue(secondaryValue)
      : String(secondaryValue);

  return (
    <div className={cn(wrapperVariants({ variant }))}>
      <div className={cn(containerVariants({ variant }))}>
        <span className={cn(labelVariants({ variant }))} title={label}>
          {label}
        </span>
        <div className={cn(valueContainerVariants({ variant }))}>
          {isMainVariant && (
            <div className="h-4 w-4 shrink-0">
              <BoltIcon />
            </div>
          )}
          <span>{formattedValue}</span>
          {unit && <span className={cn(unitVariants({ variant }))}>{unit}</span>}
        </div>
      </div>

      {!isMainVariant && isNumericSecondaryValue && (
        <div className={cn(metricBarWrapperVariants({ variant }))}>
          {showMetricBar && (
            <div className="w-[120px] flex-shrink-0">
              <MetricBar percentage={contributionPercent} />
            </div>
          )}
          <div className={cn(changeBadgeVariants({ isPositive }))}>
            <div className={cn(changeTextVariants({ isPositive }))}>{formattedContribution}</div>
          </div>
        </div>
      )}

      {isMainVariant && isNumericSecondaryValue && (
        <div className={cn(changeDisplayVariants({ variant }))}>
          {formattedSecondaryValue} {secondaryUnit ?? unit}
        </div>
      )}
    </div>
  );
}
